// Import Libraries
import React, { useState, useEffect, useContext, useRef } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faCheckSquare,
  faTrash,
  faLink,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';

// Import Components
import ProFlag from '../Elements/ProFlag.js';
import ProPill from '../Elements/ProPill.js';
import Select from '../Elements/Select';
import UpgradeMembership from '../Modals/Subscription/upgradeMembership.js';

// Import Utilties
import scriptService from '../../services/scriptService';
import UserService from '../../services/userService';
import { computeScenePermissions } from '../../hooks/useScenePermissions.js';
import analyticsService from '../../services/analyticsService.js';

// Context
import { UserContext } from '../../context/User';
import ScriptContext from '../../context/Script';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';

// Setup Modal to Root
Modal.setAppElement('#root');

// Large-export guidance thresholds (see requirements: show inline guidance
// once a processing export has gone this long with no progress AND is big
// enough that "big export, might be slow/stuck" is a plausible explanation).
const STUCK_MINUTES = 3;
const STUCK_SHOT_THRESHOLD = 800;
const STUCK_SHOT_THRESHOLD_WITH_IMAGES = 450;

/**
 * Tracks minutes-since-last-progress-update for a row, anchored off the
 * server-computed `stalled_seconds` (TIMESTAMPDIFF against updated_at) so it
 * survives reopening the modal/page, rather than restarting from 0 every time
 * this component mounts. Re-anchors whenever a fresh update lowers
 * stalled_seconds (a real progress/stage push arrived).
 */
function useStalledMinutes(row) {
  const anchorRef = useRef(Date.now());
  const lastSecondsRef = useRef(null);
  const [minutes, setMinutes] = useState(0);

  useEffect(() => {
    const seconds = typeof row?.stalled_seconds === 'number' ? row.stalled_seconds : null;
    if (seconds !== null && (lastSecondsRef.current === null || seconds < lastSecondsRef.current)) {
      anchorRef.current = Date.now() - seconds * 1000;
    }
    lastSecondsRef.current = seconds;
  }, [row?.stalled_seconds]);

  useEffect(() => {
    const tick = () => setMinutes((Date.now() - anchorRef.current) / 60000);
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  return minutes;
}

/**
 * Inline "this export is big and may be slow/stuck" nudge, rendered as a
 * full-width row directly under a processing export's row.
 */
function StuckExportGuidance({ row, dismissed, onKeepWaiting, onCancelSplit, colSpan }) {
  const stalledMinutes = useStalledMinutes(row);

  const shotCount = Number(row?.shot_count) || 0;
  const includesImages = !!row?.includes_images;
  const isLarge =
    shotCount > STUCK_SHOT_THRESHOLD ||
    (includesImages && shotCount > STUCK_SHOT_THRESHOLD_WITH_IMAGES);

  if (dismissed || !isLarge || stalledMinutes < STUCK_MINUTES) return null;

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ borderTop: 'none', pt: 0 }}>
        <Alert
          severity="warning"
          sx={{ alignItems: 'flex-start' }}
          action={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => onCancelSplit(row)}>
                Cancel &amp; split export
              </Button>
              <Button size="small" onClick={() => onKeepWaiting(row.id)}>
                Keep waiting
              </Button>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Whoa, that's one long shot list! 🎬
          </Typography>
          <Typography variant="body2">
            Big exports can take a while (or get stuck). Try exporting the first half of your
            scenes, then the second half.
          </Typography>
        </Alert>
      </TableCell>
    </TableRow>
  );
}

// Construct Component
// `section` ('history' | 'new') opens the modal on Export History or on
// Create New Export; `selectedExportId` highlights that export in the history.
// Both are optional -- without them the modal keeps its last state.
function ScriptExport({ modalIsOpen, setModalIsOpen, section, selectedExportId }) {
  const modalBodyRef = useRef(null);
  const selectedRowRef = useRef(null);
  const { user } = useContext(UserContext);
  const { script, setScript } = useContext(ScriptContext);
  const { isMobile } = useContext(SessionContext);
  const [errorMsg, setErrorMsg] = useState('');
  const [checkedScenes, setCheckedScenes] = useState({});
  const [isExportDisabled, setIsExportDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    description: true,
    allFields: true,
    includeImages: true,
    imageSize: 'Medium',
    format: 'PDF',
    removeBranding: false,
  });
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null); // { row, mode: 'cancel' | 'split' }
  const [dismissedStuckIds, setDismissedStuckIds] = useState(() => new Set());
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFeatureMsg, setUpgradeFeatureMsg] = useState(
    'Upgrade to Pro to export this scene.',
  );
  // Prior Exports Information
  const [exportsLoading, setExportsLoading] = useState(false);
  const [exportsError, setExportsError] = useState('');
  const { projectExports, setProjectExports } = useContext(ProjectViewContext);

  const EXPORT_HINT =
    'If your export takes longer than a minute, feel free to grab a coffee and check back later. You can safely close this page—we’ll keep working on it in the background';

  const scrollModalToTop = () => {
    const el = modalBodyRef.current;
    if (el) {
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        el.scrollTop = 0;
      }
    }
  };

  const handleCheckboxChange = (sceneId) => {
    setCheckedScenes((prevState) => ({
      ...prevState,
      [sceneId]: !prevState[sceneId],
    }));
  };

  const downloadExport = async (row) => {
    if (!row?.export_path || String(row.status).toLowerCase() !== 'completed') return;

    try {
      setDownloadingId(row.id);
      scriptService.getScriptExport(row.id, script.id, exportOptions);
    } catch (e) {
      console.error('downloadExport error:', e);
      toast.error('Failed to download the file.');
    } finally {
      setDownloadingId(null);
    }
  };

  const copyExportLink = async (row) => {
    if (!row?.id || String(row.status).toLowerCase() !== 'completed') return;

    try {
      const url = `${window.location.origin}/view/${row.id}?utm_medium=shotlistsharedvialink`;

      let copied = false;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch {
          // clipboard API denied (common on mobile) — fall through to prompt
        }
      }
      if (copied) {
        toast.success('Link copied to clipboard.');
      } else {
        window.prompt('Copy this link:', url);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to copy link.');
    }
  };

  const deleteExport = async (attachmentId) => {
    try {
      if (!attachmentId) {
        toast.error('Unable to delete: missing required information.');
        return;
      }

      setDeletingId(attachmentId);
      const resp = await scriptService.deleteReport(attachmentId);
      if (!resp?.success) {
        toast.error('Failed to delete export.');
        return;
      }

      setProjectExports((prev) =>
        (prev || []).filter((r) => {
          const rid = r.attachment_id ?? r.id;
          return String(rid) !== String(attachmentId);
        }),
      );

      toast.success('Export deleted.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete export.');
    } finally {
      setDeletingId(null);
    }
  };

  const cancelExport = async (row, { split } = {}) => {
    const jobId = row?.id;
    try {
      if (!jobId) {
        toast.error('Unable to cancel: missing required information.');
        return;
      }

      setCancelingId(jobId);
      const resp = await scriptService.cancelExport(jobId);
      if (!resp?.success) {
        toast.error('Failed to cancel export.');
        return;
      }

      setProjectExports((prev) => (prev || []).filter((r) => String(r.id) !== String(jobId)));
      setDismissedStuckIds((prev) => {
        if (!prev.has(jobId)) return prev;
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });

      if (split) {
        const scenes = Array.isArray(row?.request_json?.scenes) ? row.request_json.scenes : [];
        if (scenes.length > 0) {
          const half = Math.max(1, Math.ceil(scenes.length / 2));
          const firstHalfIds = scenes.slice(0, half).map(String);
          const newCheckedScenes = {};
          (script.scenes || []).forEach((scene) => {
            newCheckedScenes[scene.id] = firstHalfIds.includes(String(scene.id));
          });
          setCheckedScenes(newCheckedScenes);
          toast.info('Export canceled. The first half of your scenes is pre-selected below.');
        } else {
          toast.success('Export canceled. You can start a new one right away.');
        }
        setRequestedOpen(false);
        scrollModalToTop();
      } else {
        toast.success('Export canceled. You can start a new one right away.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to cancel export.');
    } finally {
      setCancelingId(null);
      setCancelConfirm(null);
    }
  };

  async function getSceneList() {
    let scenesToExport = [];
    for (const [key, value] of Object.entries(checkedScenes)) {
      if (value) {
        scenesToExport.push(key);
      }
    }
    const beforeLockFilter = scenesToExport.length;
    scenesToExport = scenesToExport.filter((sceneId) => {
      const scene = script.scenes?.find((s) => String(s.id) === String(sceneId));
      return !computeScenePermissions(scene, script).isSceneLocked;
    });
    if (scenesToExport.length < beforeLockFilter) {
      analyticsService.trackEvent('export_locked_scene_attempted');
    }
    if (scenesToExport.length === 0) {
      setErrorMsg('You must select at least one scene to export.');
      return [];
    } else {
      return scenesToExport;
    }
  }

  async function exportScript() {
    // validate selection first
    const scenesToExport = await getSceneList();
    if (!Array.isArray(scenesToExport) || scenesToExport.length === 0) {
      toast.error('You must select at least one scene to export.');
      scrollModalToTop(); // 🆕 ensure error is visible at the top
      return;
    }

    const opts = exportOptions.includeImages
      ? exportOptions
      : { ...exportOptions, imageSize: 'None' };

    const task = async () => {
      const logId = UserService.addActionLog('Export Script', script.id, 'script', scenesToExport);
      await scriptService.triggerScriptExport(script.id, scenesToExport, opts);
      UserService.completeActionLog(logId);
      await loadRequestedExports();
      return true;
    };

    try {
      // 🆕 Always open the export history + scroll to top on *every* click
      setRequestedOpen(true);
      scrollModalToTop();

      await toast.promise(task(), {
        pending: {
          render: 'Requesting your export…',
          type: 'info',
          isLoading: false,
          icon: 'ℹ️',
        },
        success: {
          render:
            'Your export is being prepared. Large exports may take a few minutes. You may safely close this dialogue and continue working. Find your completed exports by clicking the “Export” button again.',
          type: 'default',
          className: 'toast-custom',
          progressClassName: 'toast-custom-progress',
          icon: '✨',
        },
        error: {
          render: 'We were unable to export your shotlist. Please try again later.',
          type: 'error',
        },
      });
    } catch (err) {
      console.error('exportScript failed:', err);
      // 🆕 make sure we still scroll on error to show any messages
      scrollModalToTop();
    }
  }

  function selectAll() {
    let newCheckedScenes = {};
    script.scenes.forEach((scene) => {
      newCheckedScenes[scene.id] = !computeScenePermissions(scene, script).isSceneLocked;
    });
    setCheckedScenes(newCheckedScenes);
  }

  function selectNone() {
    let newCheckedScenes = {};
    script.scenes.forEach((scene) => {
      newCheckedScenes[scene.id] = false;
    });
    setCheckedScenes(newCheckedScenes);
  }

  function selectWithShots() {
    let newCheckedScenes = {};
    script.scenes.forEach((scene) => {
      const hasShots =
        scene.shot_list &&
        scene.shot_list[0] &&
        (scene.shot_list.length > 1 || scene.shot_list[0].id !== 0);
      newCheckedScenes[scene.id] =
        !!hasShots && !computeScenePermissions(scene, script).isSceneLocked;
    });
    setCheckedScenes(newCheckedScenes);
  }

  const toggleExportOptions = (field) => {
    setExportOptions((prevExportOptions) => ({
      ...prevExportOptions,
      [field]: !prevExportOptions[field],
    }));
  };

  // Define the handleImageSizeChange function
  const handleImageSizeChange = (size) => {
    setExportOptions((prevOptions) => ({
      ...prevOptions,
      imageSize: size,
    }));
  };

  const changeFormatDirect = (newFormat) => {
    if (!user || user.level.toLowerCase() !== 'pro') return;
    setExportOptions((prevExportOptions) => ({
      ...prevExportOptions,
      format: newFormat,
    }));
  };

  // Both this modal and UpgradeMembership are separate react-modal instances
  // sharing the same overlay z-index, so having both open at once just stacks
  // them in DOM order -- hide this one while the upgrade modal is up, and
  // restore it when the upgrade modal is dismissed (see onRequestClose below).
  const openUpgradeModal = (msg) => {
    setUpgradeFeatureMsg(msg);
    setModalIsOpen(false);
    setUpgradeModalOpen(true);
  };

  const toggleRemoveBranding = () => {
    if (!user || user.level.toLowerCase() !== 'pro') {
      analyticsService.trackEvent('upgrade_cta_clicked', { source: 'export_remove_branding' });
      openUpgradeModal('Upgrade to Pro to remove ShotKraft branding from your exports.');
      return;
    }
    toggleExportOptions('removeBranding');
  };

  function useDebounced(value, delay = 250) {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
      const t = setTimeout(() => setV(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return v;
  }

  const ProcessingProgress = React.memo(({ row }) => {
    const raw = Number(row?.progress_percent);
    const pctClamped = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : null;
    const pct = useDebounced(pctClamped, 250);

    const text =
      pct !== null ? `${pct}% complete — ${EXPORT_HINT}` : `Preparing export — ${EXPORT_HINT}`;

    // Kept in a ref (rather than passed as the Tooltip's `title` node directly) so
    // updating the text doesn't remount the Popper — that would flicker/reposition
    // the tooltip on every progress tick.
    const textRef = React.useRef(text);
    textRef.current = text;

    const titleRef = React.useRef(null);
    const setTitleRef = React.useCallback((node) => {
      titleRef.current = node;
      // The title div only mounts once the tooltip opens, so set its text
      // immediately on mount instead of waiting for the next `pct` change —
      // otherwise it can render as an empty bubble until progress ticks again.
      if (node) node.textContent = textRef.current;
    }, []);
    const titleEl = React.useMemo(() => <div ref={setTitleRef} />, [setTitleRef]);
    React.useEffect(() => {
      if (!titleRef.current) return;
      titleRef.current.textContent = text;
    }, [text]);

    return (
      <Tooltip
        title={titleEl}
        placement="bottom"
        disableFocusListener
        disableTouchListener
        TransitionProps={{ timeout: 0 }}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'white',
              color: 'black',
              boxShadow: 2,
              fontSize: 14,
              border: '1px solid #333',
              maxWidth: 360,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.25,
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 140,
            height: 20,
            overflow: 'hidden',
            display: 'inline-block',
            verticalAlign: 'middle',
            borderRadius: 1.5,
            cursor: 'default',
            color: '#1B134A',
          }}
        >
          <LinearProgress
            variant={pct !== null ? 'determinate' : 'indeterminate'}
            value={pct ?? undefined}
            sx={{
              height: '100%',
              borderRadius: 1.5,
              backgroundColor: '#E0DBFF',
              '& .MuiLinearProgress-bar': { backgroundColor: '#4835E8' },
            }}
          />

          <InfoOutlinedIcon
            sx={{
              position: 'absolute',
              left: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 14,
              color: '#1B134A',
              opacity: 1,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />

          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              inset: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: '#1B134A',
              bgcolor: 'rgba(255,255,255,0.82)',
              borderRadius: 1,
              pointerEvents: 'none',
              pl: 2.8,
            }}
          >
            {pct !== null ? `${pct}% Complete` : 'Preparing…'}
          </Typography>
        </Box>
      </Tooltip>
    );
  });

  const StatusChipWithHint = ({ row }) => {
    return (
      <Box sx={{ position: 'relative', width: 140, display: 'flex', justifyContent: 'center' }}>
        <Tooltip
          title={EXPORT_HINT}
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'white',
                color: 'black',
                boxShadow: 2,
                fontSize: 14,
                border: '1px solid #333',
                maxWidth: 360,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                lineHeight: 1.25,
              },
            },
          }}
        >
          <span>{statusChip('queued', true)}</span>
        </Tooltip>
      </Box>
    );
  };

  // Retrieve prior exports and related info
  const loadRequestedExports = async () => {
    if (!script?.id) return;
    try {
      setExportsLoading(true);
      setExportsError('');
      const data = await scriptService.listRequestedExports(script.id);
      setProjectExports(data || []);
    } catch (e) {
      console.error(e);
      setExportsError('Failed to load export history.');
    } finally {
      setExportsLoading(false);
    }
  };

  // Check for new export details when modal opens
  useEffect(() => {
    if (modalIsOpen) loadRequestedExports();
  }, [modalIsOpen, script?.id]);

  // Color coding for export status
  const statusChip = (s, withInfoIcon = false) => {
    const map = {
      queued: { color: 'default', label: 'Preparing Export' },
      running: { color: 'info', label: 'Building Export' },
      processing: { color: 'info', label: 'Building Export' },
      completed: { color: 'success', label: 'Ready' },
      failed: { color: 'error', label: 'Failed' },
    };
    const cfg = map[s || ''] || { color: 'default', label: s || 'Unknown' };
    return (
      <Chip
        size="small"
        color={cfg.color}
        label={cfg.label}
        icon={
          withInfoIcon ? <InfoOutlinedIcon sx={{ fontSize: 14, color: '#1B134A' }} /> : undefined
        }
        sx={{ '& .MuiChip-icon': { color: '#1B134A' } }}
      />
    );
  };

  // Date Formatting for export details (Today / Yesterday / date)
  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const localDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const oneDay = 24 * 60 * 60 * 1000;
    const diff = today.getTime() - localDateOnly.getTime();

    let prefix;
    if (diff === 0) {
      prefix = 'Today';
    } else if (diff === oneDay) {
      prefix = 'Yesterday';
    } else {
      prefix = d.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    }

    const time = d
      .toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();

    return `${prefix}, ${time}`;
  };

  useEffect(() => {
    selectWithShots();
  }, [script]);

  useEffect(() => {
    async function checkExport() {
      let scenesToExport = await getSceneList();
      if (scenesToExport.length > 0) {
        setErrorMsg('');
        setIsExportDisabled(false);
      } else {
        setErrorMsg('You must select at least one scene to export.');
        setIsExportDisabled(true);
      }
    }
    checkExport();
  }, [checkedScenes]);

  useEffect(() => {
    if (modalIsOpen && section) setRequestedOpen(section === 'history');
  }, [modalIsOpen, section, selectedExportId]);

  // Bring the selected export into view once the history has loaded and the
  // accordion has finished expanding (after the scroll-to-top below).
  useEffect(() => {
    if (!modalIsOpen || !selectedExportId || exportsLoading) return;
    const id = setTimeout(() => {
      selectedRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 300);
    return () => clearTimeout(id);
  }, [modalIsOpen, selectedExportId, exportsLoading]);

  // Scroll to top after accordion has been opened
  useEffect(() => {
    if (modalIsOpen && requestedOpen) {
      const id = setTimeout(() => {
        scrollModalToTop();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [modalIsOpen, requestedOpen]);

  const readyCount = (projectExports || []).filter((row) => {
    const status = String(row?.status || '').toLowerCase();
    return status === 'completed';
  }).length;

  const processingCount = (projectExports || []).filter((row) => {
    const status = String(row?.status || '').toLowerCase();
    return ['queued', 'running', 'processing'].includes(status);
  }).length;

  let exportStatusTag = null;

  if (!exportsLoading) {
    if (!projectExports || projectExports.length === 0) {
      // grey: no exports yet
      exportStatusTag = (
        <Box
          component="span"
          sx={{
            ml: 1,
            px: 1.4,
            py: 0.2,
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: '#f3f3f3',
            color: '#666',
            textTransform: 'none',
          }}
        >
          No exports yet
        </Box>
      );
    } else if (processingCount > 0) {
      // purple: X processing
      exportStatusTag = (
        <Box
          component="span"
          sx={{
            ml: 1,
            px: 1.4,
            py: 0.2,
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: '#EDE9FF',
            color: '#4835E8',
            textTransform: 'none',
          }}
        >
          {processingCount} processing
        </Box>
      );
    } else if (readyCount > 0) {
      // green: X ready
      exportStatusTag = (
        <Box
          component="span"
          sx={{
            ml: 1,
            px: 1.4,
            py: 0.2,
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: '#E9F6EC',
            color: '#2E7D32',
            textTransform: 'none',
          }}
        >
          {readyCount} ready
        </Box>
      );
    }
  }

  const isProUser = !!(user && user.level && user.level.toLowerCase() === 'pro');

  return (
    <div>
      <UpgradeMembership
        isOpen={upgradeModalOpen}
        onRequestClose={() => {
          setUpgradeModalOpen(false);
          setModalIsOpen(true);
        }}
        featureMsg={upgradeFeatureMsg}
      />

      <Modal
        isOpen={modalIsOpen}
        contentLabel="Upload Script"
        className="modal wide"
        contentRef={(node) => {
          modalBodyRef.current = node;
        }}
      >
        <button className="close-button" onClick={() => setModalIsOpen(false)}>
          <FontAwesomeIcon icon={faClose} />
        </button>
        <>
          <div className="modalHeader">
            <div className="bold">Export your shotlist</div>
          </div>

          <div className="modalContent">
            <div className="fullWidthContainer card Padded">
              <Accordion
                expanded={requestedOpen}
                onChange={(_, v) => setRequestedOpen(v)}
                sx={{
                  boxShadow: 'none', // removes Paper shadow
                  '&:before': { display: 'none' }, // removes the top divider line
                  width: '100%',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1">Export History</Typography>
                    {exportStatusTag}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {exportsError && <Alert severity="error">{exportsError}</Alert>}

                  {exportsLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading…</Typography>
                    </div>
                  ) : projectExports.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No exports yet.
                    </Typography>
                  ) : (
                    <TableContainer style={{ cursor: 'default' }}>
                      <Table size="small" aria-label="requested-exports">
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: '#f5f5f5',
                            }}
                          >
                            <TableCell>Requested On</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectExports
                            ? projectExports.map((row) => {
                                const status = String(row?.status || '').toLowerCase();
                                const hasFile = !!row?.export_path;
                                const isCSV = String(row?.type || '').toUpperCase() === 'CSV';

                                const isCompleted = status === 'completed' && hasFile;
                                const isProcessing = ['processing', 'running'].includes(status);
                                const isQueued = status === 'queued';
                                const isCancelable = isProcessing || isQueued;
                                const isDeleting = deletingId === (row.attachment_id ?? row.id);
                                const isCanceling = cancelingId === row.id;

                                return (
                                  <React.Fragment key={row.id}>
                                  <TableRow
                                    hover
                                    selected={row.id === selectedExportId}
                                    ref={row.id === selectedExportId ? selectedRowRef : undefined}
                                  >
                                    <TableCell>{fmt(row.created_at)}</TableCell>
                                    <TableCell>{row.email || '—'}</TableCell>
                                    <TableCell align="right">
                                      {isCompleted ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: 1.5,
                                          }}
                                        >
                                          {!isCSV && (
                                            <Tooltip title="Share" placement="bottom">
                                              <IconButton
                                                size="small"
                                                onClick={() => copyExportLink(row)}
                                                sx={{
                                                  borderRadius: '999px',
                                                  border: '1px solid #E0E0E0',
                                                  width: 36,
                                                  height: 36,
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faLink}
                                                  style={{ fontSize: '14px' }}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          )}

                                          <Tooltip title="Download" placement="bottom">
                                            <Button
                                              variant="contained"
                                              size="small"
                                              onClick={() => downloadExport(row)}
                                              disabled={downloadingId === row.id}
                                              sx={{
                                                textTransform: 'none',
                                                borderRadius: 9999,
                                                padding: '5px 15px 3px 15px',
                                                backgroundColor: '#7A64EB',
                                                '&:hover': {
                                                  backgroundColor: '#6a55e0',
                                                },
                                              }}
                                              startIcon={
                                                <FontAwesomeIcon
                                                  icon={faDownload}
                                                  style={{ fontSize: '12px' }}
                                                />
                                              }
                                            >
                                              {isCSV ? 'CSV' : 'PDF'}
                                            </Button>
                                          </Tooltip>

                                          <Tooltip title="Delete" placement="bottom">
                                            <IconButton
                                              size="small"
                                              onClick={() => deleteExport(row.id)}
                                              disabled={isDeleting}
                                              sx={{
                                                borderRadius: '999px',
                                                border: '1px solid #E0E0E0',
                                                width: 36,
                                                height: 36,
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={faTrash}
                                                style={{ fontSize: '14px' }}
                                              />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      ) : isProcessing ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <ProcessingProgress row={row} />
                                          <Tooltip title="Cancel export" placement="bottom">
                                            <IconButton
                                              size="small"
                                              onClick={() => setCancelConfirm({ row, mode: 'cancel' })}
                                              disabled={isCanceling}
                                              sx={{
                                                borderRadius: '999px',
                                                border: '1px solid #E0E0E0',
                                                width: 32,
                                                height: 32,
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faClose} style={{ fontSize: '14px' }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      ) : isQueued ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <StatusChipWithHint row={row} />
                                          <Tooltip title="Cancel export" placement="bottom">
                                            <IconButton
                                              size="small"
                                              onClick={() => setCancelConfirm({ row, mode: 'cancel' })}
                                              disabled={isCanceling}
                                              sx={{
                                                borderRadius: '999px',
                                                border: '1px solid #E0E0E0',
                                                width: 32,
                                                height: 32,
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faClose} style={{ fontSize: '14px' }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      ) : (
                                        // failed or unknown
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: 1.5,
                                          }}
                                        >
                                          {statusChip(status)}
                                          <IconButton
                                            size="small"
                                            onClick={() => deleteExport(row.id)}
                                            disabled={isDeleting}
                                            sx={{
                                              borderRadius: '999px',
                                              border: '1px solid #E0E0E0',
                                              width: 36,
                                              height: 36,
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={faTrash}
                                              style={{ fontSize: '14px' }}
                                            />
                                          </IconButton>
                                        </Box>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  {isCancelable && (
                                    <StuckExportGuidance
                                      row={row}
                                      colSpan={3}
                                      dismissed={dismissedStuckIds.has(row.id)}
                                      onKeepWaiting={(jobId) =>
                                        setDismissedStuckIds((prev) => new Set(prev).add(jobId))
                                      }
                                      onCancelSplit={(r) =>
                                        setCancelConfirm({ row: r, mode: 'split' })
                                      }
                                    />
                                  )}
                                  </React.Fragment>
                                );
                              })
                            : null}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            </div>
            <hr />
            <div className="fullWidthContainer Padded">
              <div className="fullWidthContainer Large Padded">
                <Typography variant="subtitle1">Create New Export</Typography>
              </div>
              <div className="fullWidthContainer">
                {/* Include options */}
                <span className="OnePortionFlex Padded MinSmall">
                  <span className="fullWidthContainer" style={{ float: 'left' }}>
                    <Typography variant="subtitle2">Include</Typography>
                  </span>
                  <div className="MinExSmall" style={{ float: 'left' }}>
                    <div className="Padded">
                      <span
                        className="linkText"
                        onClick={() => toggleExportOptions('description')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon
                          icon={exportOptions.description ? faCheckSquare : faSquare}
                        />
                      </span>
                      <span> Description</span>
                    </div>
                    <div className="Padded">
                      <span
                        className="linkText"
                        onClick={() => toggleExportOptions('allFields')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon
                          icon={exportOptions.allFields ? faCheckSquare : faSquare}
                        />
                      </span>
                      <span>Shot Information</span>
                    </div>
                    <div className="Padded">
                      <div style={{ display: 'flex', flexDirection: 'column', width: 'fit-content', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            className="linkText"
                            onClick={() => toggleExportOptions('includeImages')}
                            style={{ cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon
                              icon={exportOptions.includeImages ? faCheckSquare : faSquare}
                            />
                          </span>
                          <span style={{ paddingLeft: '5px', paddingRight: '5px' }}>Images</span>
                          {exportOptions.includeImages && (
                            <Select
                              selectLabel={``}
                              labelStyle={`none`}
                              selectOptions={['Small', 'Medium', 'Large', 'Storyboard']}
                              selectedValue={exportOptions.imageSize}
                              handleSelectChange={(value) => handleImageSizeChange(value)}
                              useClass={`ImageSizeDropdown`}
                            />
                          )}
                        </div>
                        {exportOptions.includeImages &&
                          ['Large', 'Storyboard'].includes(exportOptions.imageSize) && (
                            <Alert severity="warning" sx={{ width: '100%', fontSize: 13, padding: '5px' }}>
                              This export option puts emphasis on images and may result in
                              abbreviated text.
                            </Alert>
                          )}
                      </div>
                    </div>
                    <div className="Padded">
                      <span
                        className="linkText"
                        onClick={toggleRemoveBranding}
                        style={{
                          cursor: isProUser ? 'pointer' : 'not-allowed',
                          opacity: isProUser ? 1 : 0.6,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={exportOptions.removeBranding ? faCheckSquare : faSquare}
                        />
                      </span>
                      <span style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                        Remove ShotKraft branding
                      </span>
                      {!isProUser && (
                        <ProPill
                          tooltip="Upgrade to Pro to remove ShotKraft branding from your exports."
                          onClick={toggleRemoveBranding}
                        />
                      )}
                    </div>
                  </div>
                </span>

                {/* Format options with MUI padded radios */}
                <span className="OnePortionFlex Padded popoverHover" style={{ minWidth: '150px' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Format
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {['PDF', 'CSV'].map((fmt) => {
                      const selected = exportOptions.format === fmt;
                      return (
                        <Box
                          key={fmt}
                          onClick={() => changeFormatDirect(fmt)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 9999,
                            border: '1px solid',
                            borderColor: selected ? '#7A64EB' : '#E0E0E0',
                            backgroundColor: selected ? 'rgba(122,100,235,0.06)' : '#FFFFFF',
                            cursor: isProUser ? 'pointer' : 'not-allowed',
                            opacity: isProUser ? 1 : 0.5,
                          }}
                        >
                          <Radio
                            checked={selected}
                            value={fmt}
                            disabled={!isProUser}
                            size="small"
                            sx={{ mr: 1, p: 0.25 }}
                          />
                          <Typography variant="body2">{fmt}</Typography>
                          {fmt === 'CSV' && (
                            <ProFlag
                              type="user"
                              scriptLevel={script.features}
                              script={script.id}
                              handleUpgrade={() =>
                                setScript((prev) => ({
                                  ...prev,
                                  features: 'Pro',
                                }))
                              }
                              onModalVisibilityChange={(open) => setModalIsOpen(!open)}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </span>
              </div>
            </div>

            <hr />

            <div className="fullWidthContainer">
              <Typography variant="subtitle2" className="Padded">
                Select Scenes to Export
              </Typography>
            </div>
            <div className="fullWidthContainer Padded">
              <button
                style={{ marginBottom: '20px' }}
                className="TwoPortionFlex MinExSmall"
                onClick={() => selectAll()}
              >
                Select All
              </button>
              <button
                style={{ marginBottom: '20px' }}
                className="TwoPortionFlex MinExSmall"
                onClick={() => selectNone()}
              >
                Select None
              </button>
              <button
                style={{ marginBottom: '20px' }}
                className="ThreePortionFlex MinExSmall"
                onClick={() => selectWithShots()}
              >
                Select With Shots
              </button>
              <span className="OnePortionFlex exportModalSpacing">&nbsp;</span>
              {isLoading ? (
                <div className="MinSmall" style={{ float: 'right' }}>
                  Requesting Export...
                  <img
                    style={{
                      width: '50px',
                      maxWidth: '50px',
                      float: 'right',
                      marginTop: '-15px',
                    }}
                    src="/Spinner-2.9s-297px.gif"
                    className="smallLoadingGif"
                    alt="Loading.."
                  />
                </div>
              ) : (
                <button
                  style={{ float: 'right', marginBottom: '20px' }}
                  className="OnePortionFlex buttonClone MinSmall"
                  disabled={isExportDisabled}
                  onClick={() => exportScript()}
                >
                  Export Shotlist
                </button>
              )}
            </div>
          </div>
          <div>
            {script.scenes?.map((scene) => {
              const { isSceneLocked } = computeScenePermissions(scene, script);
              return (
              <div
                className="fullWidthContainer"
                key={scene.id}
                style={{
                  margin: '0px 10px 0px 10px',
                  borderBottom: '1px solid #DEDEDE',
                  padding: '5px 0px 5px 0px',
                  opacity: isSceneLocked ? 0.5 : 1,
                }}
              >
                <div
                  className="OnePortionFlex cardTop Left"
                  style={{ cursor: isSceneLocked ? 'not-allowed' : 'pointer' }}
                  onClick={() => !isSceneLocked && handleCheckboxChange(scene.id)}
                >
                  {checkedScenes[scene.id] ? (
                    <FontAwesomeIcon className="linkText" icon={faCheckSquare} />
                  ) : (
                    <FontAwesomeIcon className="linkText" icon={faSquare} />
                  )}
                </div>
                <div className="OnePortionFlex cardTop">{scene.scene_number}</div>
                <div className="TwoPortionFlex">{scene.scene_int_ext}</div>
                <div className="EightPortionFlex">
                  {scene.header_text}
                  {isSceneLocked && (
                    <ProPill
                      tooltip="Free plan exports scenes 1–5. Upgrade to Pro to export this scene."
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        analyticsService.trackEvent('upgrade_cta_clicked', {
                          source: 'export_lock',
                        });
                        openUpgradeModal('Upgrade to Pro to export this scene.');
                      }}
                    />
                  )}
                </div>
                <div className="TwoPortionFlex">{scene.scene_time_of_day}</div>
                <div className="TwoPortionFlex">
                  {scene.shot_list &&
                  (scene.shot_list.length > 1 ||
                    (scene.shot_list[0] && scene.shot_list[0].id !== 0)) ? (
                    <span className="purpleText bold">{scene.shot_list.length} Shots</span>
                  ) : (
                    <span>0 Shots</span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          <div className="fullWidthContainer errorText" style={{ paddingTop: '30px' }}>
            {errorMsg}
          </div>
          <div className="fullWidthContainer" style={{ paddingTop: '30px' }}>
            <span className="OnePortionFlex"></span>

            {isLoading ? (
              <div className="OnePortionFlex MinSmall">
                <div className="MinSmall" style={{ float: 'right' }}>
                  Requesting Export...
                  <img
                    style={{ width: '50px', maxWidth: '50px', float: 'right', margin: '0 auto' }}
                    src="/Spinner-2.9s-297px.gif"
                    className="smallLoadingGif"
                    alt="Loading.."
                  />
                </div>
              </div>
            ) : (
              <button
                className="OnePortionFlex buttonClone MinSmall"
                disabled={isExportDisabled}
                onClick={() => exportScript()}
              >
                Export Shotlist
              </button>
            )}
          </div>
        </>
      </Modal>

      {/* MUI dialogs default to z-index 1300, but the react-modal overlay above
          sits at 1301 (modalStyles.css), so lift this confirm above it. */}
      <Dialog
        open={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        sx={{ zIndex: 1302 }}
      >
        <DialogTitle>Cancel this export?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can start a new one right away.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2.5, pr: 3 }}>
          <Button
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              color: '#0E0F11',
              border: '1px solid #cccccc',
              '&:hover': { backgroundColor: '#F5F5F5', border: '1px solid #cccccc' },
            }}
            onClick={() => setCancelConfirm(null)}
          >
            Keep waiting
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelingId === cancelConfirm?.row?.id}
            onClick={() =>
              cancelExport(cancelConfirm.row, { split: cancelConfirm.mode === 'split' })
            }
          >
            Cancel export
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ScriptExport;
